import { useApolloClient, useMutation } from '@apollo/client';
import type { VariablesOf } from '@graphql-typed-document-node/core';
import moment from 'moment';
import { useCallback, useEffect } from 'react';
import useAppSelector from '../app/redux/useAppSelector';
import { ACTION_POST_READ, CATEGORY_PRODUCT } from '../constants/analytics';
import { gql } from '../graphql/generated/gql';
import type { PostEdge, User } from '../graphql/generated/graphql';
import { track } from '../utils/analytics';

const INACTIVITY_TIMEOUT = 30000;
const MIN_ACTIVITY_READ = 10000;
const MIN_ANALYTICS_READ = 30000;

const READ_POST_MUTATION = gql(/** @complexity 2 */ `
  mutation readPost(
    $postId: ID!
    $time: Int!
    $queryId: String
    $resultId: String
  ) {
    readPost(id: $postId, time: $time, queryId: $queryId, resultId: $resultId) {
      id
    }
  }
`);

// eslint-disable-next-line slab-graphql/no-unused-fields
const postReadUpdateFragment = gql(`
  # eslint-disable-next-line @graphql-eslint/require-selections
  fragment PostReadUpdateFragment on Post {
    userViewedAt
  }
`);

export const useReadPost = () => {
  const [readPost] = useMutation(READ_POST_MUTATION);
  const currentUser = useAppSelector((state) => state.session.user);

  return useCallback(
    (variables: VariablesOf<typeof READ_POST_MUTATION>) => {
      const { postId } = variables;
      return readPost({
        variables,
        optimisticResponse: { readPost: { id: postId } },
        update: (cache) => {
          const postCacheId = cache.identify({
            id: postId,
            __typename: 'Post',
          });

          cache.updateFragment(
            { id: postCacheId, fragment: postReadUpdateFragment },
            (existing) =>
              existing && { ...existing, userViewedAt: moment().toISOString() },
          );

          cache.modify<User>({
            id: cache.identify(currentUser),
            fields: {
              viewedPosts(postConnection, { isReference }) {
                if (
                  !postConnection ||
                  isReference(postConnection) ||
                  postConnection.edges == null
                ) {
                  return postConnection;
                }

                const edges = postConnection.edges
                  .filter(
                    (
                      edge,
                    ): edge is PostEdge & {
                      node: NonNullable<PostEdge['node']>;
                    } => edge?.node != null,
                  )
                  // move the post to the top
                  .toSorted((edgeA, edgeB) => {
                    if (postCacheId == null) return 0;

                    // Note: The type provided by the cache does not reflect the reality
                    // See this comment: https://github.com/apollographql/apollo-client/issues/7191#issuecomment-712407692
                    // We can make use of the fact that `cache.identify` can take both the reference or the object
                    // to identify it and return the cache id. This way, we don't need to do anything to fix the types.
                    if (cache.identify(edgeA.node) === postCacheId) return -1;
                    if (cache.identify(edgeB.node) === postCacheId) return 1;
                    return 0;
                  });

                return {
                  ...postConnection,
                  edges,
                };
              },
            },
          });
        },
      }).catch(() => {});
    },
    [readPost, currentUser],
  );
};

const usePostActivityTracker = (
  postId: string | null,
  queryId?: string,
  resultId?: string,
) => {
  const currentUser = useAppSelector((state) => state.session.user);
  const client = useApolloClient();
  const readPost = useReadPost();

  useEffect(() => {
    if (!postId) {
      return undefined;
    }
    let readStart: number | null = null;
    let lastActive: number | null = null;
    let inactiveInterval: NodeJS.Timeout | null = null;

    const sendRead = (endTime: number) => {
      if (readStart === null) return;
      const time = endTime - readStart;
      if (time > MIN_ACTIVITY_READ) {
        readPost({ postId, time, queryId, resultId });
      }
      if (time > MIN_ANALYTICS_READ) {
        track(ACTION_POST_READ, {
          category: CATEGORY_PRODUCT,
          label: postId,
          value: time,
        });
      }
    };

    const onInactive = () => {
      const now = moment().valueOf();
      if (lastActive !== null && now - lastActive > INACTIVITY_TIMEOUT * 1.2) {
        // Wake detected
        sendRead(lastActive);
      } else {
        sendRead(now);
      }
      if (inactiveInterval) {
        clearInterval(inactiveInterval);
      }
      inactiveInterval = null;
      readStart = null;
    };

    const onActive = () => {
      readStart = moment().valueOf();
      lastActive = moment().valueOf();
      inactiveInterval = setInterval(() => {
        if (moment().valueOf() - lastActive! > INACTIVITY_TIMEOUT) {
          onInactive();
        }
      }, INACTIVITY_TIMEOUT);
    };

    const onActivity = () => {
      if (readStart === null) {
        onActive();
      }
      const now = moment().valueOf();
      if (lastActive !== null && now - lastActive > INACTIVITY_TIMEOUT * 1.2) {
        // Wake detected
        sendRead(lastActive);
        readStart = now;
      }
      lastActive = now;
    };

    onActive();

    document.addEventListener('mousemove', onActivity);
    document.addEventListener('touchstart', onActivity);

    return () => {
      onInactive();
      if (inactiveInterval) {
        clearInterval(inactiveInterval);
      }
      document.removeEventListener('mousemove', onActivity);
      document.removeEventListener('touchstart', onActivity);
    };
  }, [postId, queryId, resultId, readPost, client, currentUser]);
};

interface PostActivityTrackerProps {
  postId: string | null;
  queryId?: string;
  resultId?: string;
}

const PostActivityTracker = ({
  postId,
  queryId,
  resultId,
}: PostActivityTrackerProps) => {
  usePostActivityTracker(postId, queryId, resultId);

  return null;
};

export default PostActivityTracker;
